import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { Library, FileText, Eye, Plus, ArrowRight } from "lucide-react";

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [stats, setStats] = useState({ spaces_count: 0, articles_count: 0, views_this_month: 0, recent_articles: [] });
  const [org, setOrg] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data));
    api.get("/org").then((r) => setOrg(r.data));
  }, []);

  const cards = [
    { label: "Spaces", value: stats.spaces_count, icon: Library, color: "bg-[#41B5E6]" },
    { label: "Articles", value: stats.articles_count, icon: FileText, color: "bg-emerald-600" },
    { label: "Views (30d)", value: stats.views_this_month, icon: Eye, color: "bg-amber-500" },
  ];

  return (
    <div className="p-6 sm:p-10 max-w-7xl">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 font-bold">Welcome back</div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="dashboard-title">
          {user?.full_name?.split(" ")[0]} · {org?.name || "Your workspace"}
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g, "")}`}
            className="bg-white border border-zinc-200 p-5 rounded-sm flex items-center gap-4">
            <div className={`${c.color} w-12 h-12 rounded-sm flex items-center justify-center`}>
              <c.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-3xl font-black tracking-tight">{c.value}</div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="font-bold tracking-tight">Recent activity</h2>
            <Link to="/spaces" className="text-xs text-[#41B5E6] hover:underline font-semibold">All spaces →</Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {stats.recent_articles.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">No activity yet. Create your first article.</div>
            ) : (
              stats.recent_articles.map((a) => (
                <Link key={a.id} to={`/spaces/${a.space_id}/articles/${a.id}`}
                  className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors">
                  <div>
                    <div className="font-semibold text-sm">{a.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded-sm mr-2 text-[10px] uppercase tracking-wider font-bold ${
                        a.status === "published" ? "bg-emerald-100 text-emerald-800" :
                        a.status === "draft" ? "bg-zinc-100 text-zinc-700" : "bg-zinc-200 text-zinc-600"
                      }`}>{a.status}</span>
                      Updated {new Date(a.updated_at).toLocaleString()}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-400" />
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <h2 className="font-bold tracking-tight mb-4">Quick actions</h2>
          <div className="space-y-2">
            <Link to="/spaces" data-testid="quick-create-space"
              className="flex items-center gap-3 px-3 py-2.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors">
              <Plus className="w-4 h-4" />
              <span className="text-sm font-semibold">New Space</span>
            </Link>
            <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors">
              <span className="text-sm font-semibold">Invite teammates</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
